import { useRouter } from "next/router";

import {
  objectTypes,
  actionNames,
  useTracker,
  eventTypes,
} from "@newsela/snitch";
import useCreateOrUpdatePoll from "@/view/utils/useCreateOrUpdatePoll";
import useDeletePoll from "@/view/utils/useDeletePoll";

export const createPollPayload = ({ assignmentId, contentId, poll }) => ({
  ...poll,
  ...(assignmentId
    ? { assignment_id: assignmentId }
    : { content_id: contentId }),
  copied_from_activity: poll?.id,
  id: undefined, // We include this in order to override the original template poll's id
  is_template: false,
  template_display_order: null,
});

const updatePollPayload = ({
  assignmentId,
  contentId,
  poll,
  assignedPoll,
}) => ({
  ...poll,
  ...(assignmentId
    ? { assignment_id: assignmentId }
    : { content_id: contentId }),
  content_type: assignedPoll.content_type,
  created_by_id: assignedPoll.created_by_id,
  id: assignedPoll.id,
  is_template: false,
});

const useHandlePollAfterAssignmentUpdate = ({
  activePoll,
  assignedPoll,
  customPoll,
  isEditMode,
  isPollSelected,
  setAssignedPoll,
  setIsEditMode,
}) => {
  const [createOrUpdatePoll] = useCreateOrUpdatePoll();
  const [deletePoll] = useDeletePoll();
  const { trackAction, getEventName } = useTracker();

  const router = useRouter();
  const { levelId } = router.query;

  const eventData = {
    event_type: eventTypes.click,
    dimension_properties: {
      content_id: levelId,
      content_type: "Article",
      object_id: assignedPoll?.id,
    },
  };

  const handlePoll = async ({ assignmentId, contentId }) => {
    const resolvedContentId = contentId || levelId;
    if (!assignmentId && !resolvedContentId) return;

    const isUpdatingAssignedPoll = isPollSelected && assignedPoll && isEditMode;
    const isUpdatingAssignedPollFromTemplate =
      isPollSelected && assignedPoll && !isEditMode;
    const isCreatingNewCustomPoll =
      isPollSelected && isEditMode && !assignedPoll;
    const isCreatingNewPollFromTemplate =
      isPollSelected && !isEditMode && !assignedPoll;
    const isDeletingPoll = !isPollSelected && assignedPoll;

    if (isUpdatingAssignedPoll) {
      const pollPayload = updatePollPayload({
        assignmentId,
        contentId: resolvedContentId,
        assignedPoll,
        poll: {
          data: customPoll,
        },
      });

      const eventName = getEventName(
        objectTypes.poll,
        null,
        actionNames.update,
      );

      const poll = await createOrUpdatePoll(pollPayload);
      setAssignedPoll(poll?.data);
      setIsEditMode(false);
      trackAction({ ...eventData, event_name: eventName });
    }

    if (isUpdatingAssignedPollFromTemplate) {
      const pollPayload = updatePollPayload({
        assignmentId,
        contentId: resolvedContentId,
        assignedPoll,
        poll: activePoll,
      });

      const eventName = getEventName(
        objectTypes.poll,
        null,
        actionNames.update,
      );

      const poll = await createOrUpdatePoll(pollPayload);
      setAssignedPoll(poll?.data);
      trackAction({ ...eventData, event_name: eventName });
    }

    if (isCreatingNewCustomPoll) {
      const pollPayload = createPollPayload({
        assignmentId,
        contentId: resolvedContentId,
        poll: {
          content_type: "poll",
          data: customPoll,
        },
      });

      const eventName = getEventName(
        objectTypes.poll,
        null,
        actionNames.create,
      );

      const poll = await createOrUpdatePoll(pollPayload);
      setAssignedPoll(poll?.data);
      setIsEditMode(false);
      trackAction({ ...eventData, event_name: eventName });
    }

    if (isCreatingNewPollFromTemplate) {
      const pollPayload = createPollPayload({
        assignmentId,
        contentId: resolvedContentId,
        poll: activePoll,
      });

      const eventName = getEventName(
        objectTypes.poll,
        null,
        actionNames.create,
      );

      const poll = await createOrUpdatePoll(pollPayload);
      setAssignedPoll(poll?.data);
      trackAction({ ...eventData, event_name: eventName });
    }

    if (isDeletingPoll) {
      const eventName = getEventName(
        objectTypes.poll,
        null,
        actionNames.delete,
      );

      deletePoll(assignedPoll?.id);
      setAssignedPoll(null);
      trackAction({ ...eventData, event_name: eventName });
    }
  };

  return [handlePoll];
};

export default useHandlePollAfterAssignmentUpdate;
